import type { ScoreBreakdown } from '@/lib/score';
import { parseJsonArray } from '@/lib/utils';
import type { Job } from '@/types/job.type';

export interface MatchReasoning {
  compensation: number;
  explanation: string;
  highlights: string[];
  logistics: number;
  overall: number;
  redFlags: string[];
  roleFit: number;
  techMatch: number;
}

export const parseTechStack = (raw: string | null) =>
  parseJsonArray(raw).filter((v): v is string => typeof v === 'string');

export const parseMatchReasoning = (raw: string | undefined) => {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as MatchReasoning;
  } catch {
    return null;
  }
};

export const breakdownFromJob = (job: Job): ScoreBreakdown | undefined => {
  const reasoning = parseMatchReasoning(job.matchReasoning);
  if (!reasoning) {
    return undefined;
  }

  return {
    comp: reasoning.compensation,
    explanation: reasoning.explanation,
    logistics: reasoning.logistics,
    roleFit: reasoning.roleFit,
    techMatch: reasoning.techMatch,
  };
};

export const postedDaysAgo = (postedAt: string | null) => {
  if (!postedAt) {
    return null;
  }

  const ms = Date.parse(postedAt);

  if (Number.isNaN(ms)) {
    return null;
  }

  const days = Math.floor((Date.now() - ms) / 86_400_000);

  return days < 0 ? 0 : days;
};

export const relativeDateFromPostedAt = (postedAt: string | null) => {
  const days = postedDaysAgo(postedAt);

  if (days === null) {
    return null;
  }

  if (days === 0) {
    return 'today';
  }

  if (days === 1) {
    return '1d ago';
  }

  return `${days}d ago`;
};
